import { useEffect, useState } from "react";
import { ClipboardCheck, ArrowRight } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";

const ACTIVE_KEYS = new Set(["organization_created", "admin_assigned", "departments_created"]);

const STEPS = [
  ["organization_created", "Organization created"],
  ["admin_assigned", "Admin assigned"],
  ["departments_created", "Departments created"],
  ["staff_enrollment_pending", "Staff enrollment", "Phase 4"],
  ["course_mapping_pending", "Course mapping", "Phase 5"],
  ["training_pending", "Training", "Phase 6"],
  ["go_live_pending", "Go live", "Phase 9"],
  ["active_training_started", "Active training", "Live"],
];

export default function OrgOnboardingPage() {
  const [ob, setOb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/onboarding");
        setOb(data.data.onboarding);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !ob) return <DashboardLayout title="Onboarding"><LoadingBlock label="Loading…" /></DashboardLayout>;

  return (
    <DashboardLayout title="Onboarding" breadcrumbs={["My Institute", "Onboarding"]}>
      <div className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-6" data-testid="org-onboarding-page">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Setup progress</div>
        <div className="mt-2 flex items-center gap-3">
          <Progress value={ob.completion_percentage || 0} className="h-2 flex-1" />
          <span className="text-sm font-bold text-slate-900">{ob.completion_percentage || 0}%</span>
        </div>
        <ol className="mt-8 space-y-4">
          {STEPS.map(([key, label, phase], i) => {
            const done = ACTIVE_KEYS.has(key) ? ob[key] : false;
            return (
              <li key={key} className="flex items-start gap-4">
                <span className={`flex h-8 w-8 items-center justify-center rounded-full ${done ? "bg-[#329112] text-white" : "bg-slate-100 text-slate-400"}`}>
                  <ClipboardCheck className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${done ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
                    {phase && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{phase}</span>}
                  </div>
                  <div className="text-xs text-slate-500">Step {i + 1} of {STEPS.length}</div>
                </div>
                {done && <ArrowRight className="mt-2 h-4 w-4 text-[#329112]" />}
              </li>
            );
          })}
        </ol>
      </div>
    </DashboardLayout>
  );
}
